<template>
  <!--

    Shows confirm or alert dialogs upon request, centered, persistent

    This component offers two approaches
      alert: shows a one button alert dialog, used to inform the user about an error or warning etc.
      confirm: shows a confirmation dialog with a default "OK" button / "Cancel" button,
        both button texts maybe overridden by the options.positive / options.negative properties

    Both methods return a Promise

    Usage:
      this.$alert (message, options, title)
      this.$oops (message, options, title)
      this.$confirm (message, options, title)
      this.$comment (message, options, title)

    Parameters
      message: the text to be displayed in the dialog, shall tell the user why you are bothering him;
        the message can be empty, then only (default) title and the button(s) are shown
      options: possible options for layout and behaviour
        width: width of the dialog: default is 400
        zIndex: the zindex to use for the dialog, defaults to 200
        positive: text for the "positive confirmation" button
        negative: text for the "negative confirmation" button
        icon: title of an icon to show, there are defaults for alert and confirm dialogs
        color: color of the icon
      title: a piece of text to override the default dialog card title which is depended on the function call

    Examples
      // shows a one button dialog with default title and the message submitted
      this.$alert(
        'A fake error occured!'
      )
      // shows a one button dialog with title "Oops" and the message submitted
      // this function analyzes whether there is hint code "(h_xxxx)" in the message (see AdDocs.vue for more info)
      this.$oops(
        'A fake error occured!'
      )
      // shows a two button dialog with "OK" and "Cancel", default title and the message submitted
      this.$confirm(
        'Do you really want to delete the object?'
      )
      // shows a two button dialog with button texts as in the options, default title and the message submitted
      this.$confirm(
        'Are you fine?', {positive: 'yeeaaah', negative: 'nooooo'})
      )
  -->
  <v-layout>
    <!-- the confirmation dialog -->
    <v-dialog
        v-model="dialog"
        persistent
        :max-width="options.width"
        :style="{ zIndex: options.zIndex }"
        @keydown.esc="disagree"
      >
        <v-card>
          <AdCardTitle v-if="Boolean(title)" dialog>
            <v-icon :color="color" class="pr-4" v-if="icon">{{ icon }}</v-icon>
            {{ title }}
          </AdCardTitle>
          <v-card-text v-if="Boolean(message)" class="pa-5" v-html="message"/>
          <v-card-actions>
            <v-spacer></v-spacer>
              <v-btn @click.native="agree" color="primary darken-1" text>{{ positiveButton }}</v-btn>
              <v-btn @click.native="disagree" color="grey" text v-if="negativeButton">{{ negativeButton }}</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
      <!-- the oops dialog -->
      <v-dialog
        v-model="oopsy"
        persistent
        :max-width="options.width"
        :style="{ zIndex: options.zIndex }"
      >
        <v-card>
          <AdCardTitle dialog>
            <v-icon color="orange" class="pr-4">mdi-alert</v-icon>
            {{ $t('oops') }}
          </AdCardTitle>
          <v-card-text class="pa-5">
            <div v-html="message"/>
            <div class="text-right">
              <AdDocs v-if="hintCode" :hintCode="hintCode" external/>
            </div>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
              <v-btn @click.native="agree" color="primary darken-1" text>{{ this.$t('OK') }}</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
  </v-layout>
</template>

<script>

export default {
  name: 'AdAlertConfirm',
  components: {},
  computed: {
  },
  data () {
    return {
      dialog: false,
      oopsy: false,
      hintCode: null,
      resolve: null,
      reject: null,
      message: null,
      title: null,
      icon: null,
      color: '',
      options: {
        width: 400,
        zIndex: 200
      },
      positiveButton: null,
      negativeButton: null
    }
  },
  methods: {
    alert (message, options, title) {
      this.dialog = true
      this.title = title || this.$t('note')
      this.message = message
      this.options = Object.assign(this.options, options)
      this.icon = options?.icon || 'mdi-alert'
      this.color = options?.color || 'red lighten-2'
      this.positiveButton = options?.positive || this.$t('OK')
      this.negativeButton = null
      return new Promise((resolve, reject) => {
        this.resolve = resolve
        this.reject = reject
      })
    },
    oops (message, options, title) {
      // check whether the error message contains a (h_xxx) tag at the end
      const m = (typeof message === 'string' || message instanceof String) ? message : '(unknown error)'
      const hintix = m.lastIndexOf('(h_')
      // if so, extract the hint code
      this.hintCode = (hintix > 0) ? m.substring(hintix + 1, m.lastIndexOf(')')) : null
      // if so, eliminate the code from the error message
      this.message = (hintix > 0) ? m.substring(0, hintix) : m
      this.oopsy = true
      return new Promise((resolve, reject) => {
        this.resolve = resolve
        this.reject = reject
      })
    },
    async confirm (message, options, title) {
      this.dialog = true
      this.title = title || this.$t('confirm')
      this.message = message
      this.options = Object.assign(this.options, options)
      this.icon = options?.icon || 'mdi-call-split'
      this.color = options?.color || 'orange darken-1'
      this.positiveButton = options?.positive || this.$t('OK')
      this.negativeButton = options?.negative || this.$t('cancel')
      return new Promise((resolve, reject) => {
        this.resolve = resolve
        this.reject = reject
      })
    },
    agree () {
      this.resolve(true)
      this.dialog = false
      this.oopsy = false
    },
    disagree () {
      this.resolve(false)
      this.dialog = false
      this.oopsy = false
    }
  }
}
</script>
